import { useMemo } from "react";
import { differenceInCalendarDays, parseISO } from "date-fns";
import type { StageHistoryEntry } from "@/hooks/use-pipeline";
import {
  findClosedLostPipeline,
  findClosedWonPipeline,
  isTerminalPipeline,
} from "@/lib/pipeline-display";
import type { PipelineRow } from "@/types/pipeline";
import { MockProperty } from "@/data/mockProperties";
import { DollarSign, Home, Clock, TrendingUp } from "lucide-react";

interface SavedPropertyRow {
  id: string;
  pipeline_id?: string | null;
  property_data: unknown;
  created_at: string;
  updated_at: string;
}

interface PipelineAnalyticsProps {
  pipelines: PipelineRow[];
  getPropertiesByPipeline: (pipeline: PipelineRow) => SavedPropertyRow[];
  savedProperties: SavedPropertyRow[];
  stageHistory: StageHistoryEntry[];
}

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);

function calendarDaysBetween(startIso: string, endIso: string): number {
  try {
    return Math.max(0, differenceInCalendarDays(parseISO(endIso), parseISO(startIso)));
  } catch { return 0; }
}

function rowStageKey(row: SavedPropertyRow, pipelines: PipelineRow[]): string | null {
  if (!row.pipeline_id) return null;
  return pipelines.find((p) => p.id === row.pipeline_id)?.id ?? null;
}
function historyToPipelineId(h: StageHistoryEntry, pipelines: PipelineRow[]): string | null {
  if (!h.to_pipeline_id) return null;
  return pipelines.find((p) => p.id === h.to_pipeline_id)?.id ?? null;
}
function historyFromPipelineId(h: StageHistoryEntry, pipelines: PipelineRow[]): string | null {
  if (!h.from_pipeline_id) return null;
  return pipelines.find((p) => p.id === h.from_pipeline_id)?.id ?? null;
}

function lastEnteredPipelineBefore(
  propertyId: string, pipelineId: string, beforeIso: string,
  history: StageHistoryEntry[], pipelines: PipelineRow[], createdAt: string,
): string {
  const before = new Date(beforeIso).getTime();
  let best: string | null = null; let bestT = -Infinity;
  for (const h of history) {
    if (h.pipeline_property_id !== propertyId) continue;
    if (historyToPipelineId(h, pipelines) !== pipelineId) continue;
    const t = new Date(h.changed_at).getTime();
    if (t >= before) continue;
    if (t >= bestT) { bestT = t; best = h.changed_at; }
  }
  return best ?? createdAt;
}

function lastEnteredPipelineCurrent(
  propertyId: string, pipelineId: string,
  history: StageHistoryEntry[], pipelines: PipelineRow[], createdAt: string,
): string {
  const now = Date.now();
  let best: string | null = null; let bestT = -Infinity;
  for (const h of history) {
    if (h.pipeline_property_id !== propertyId) continue;
    if (historyToPipelineId(h, pipelines) !== pipelineId) continue;
    const t = new Date(h.changed_at).getTime();
    if (t > now) continue;
    if (t >= bestT) { bestT = t; best = h.changed_at; }
  }
  return best ?? createdAt;
}

function computeStageAvgDwellDays(
  pipelines: PipelineRow[], savedProperties: SavedPropertyRow[], history: StageHistoryEntry[],
): { avgDays: Record<string, number>; sampleCounts: Record<string, number> } {
  const sums: Record<string, number> = {};
  const counts: Record<string, number> = {};
  for (const p of pipelines) { sums[p.id] = 0; counts[p.id] = 0; }

  for (const p of savedProperties) {
    for (const h of history) {
      if (h.pipeline_property_id !== p.id) continue;
      const fromId = historyFromPipelineId(h, pipelines);
      if (!fromId || !(fromId in sums)) continue;
      const entered = lastEnteredPipelineBefore(p.id, fromId, h.changed_at, history, pipelines, p.created_at);
      sums[fromId] += calendarDaysBetween(entered, h.changed_at);
      counts[fromId] += 1;
    }
    const currentId = rowStageKey(p, pipelines);
    if (!currentId || !(currentId in sums)) continue;
    const entered = lastEnteredPipelineCurrent(p.id, currentId, history, pipelines, p.created_at);
    sums[currentId] += calendarDaysBetween(entered, new Date().toISOString());
    counts[currentId] += 1;
  }

  const avgDays: Record<string, number> = {};
  for (const p of pipelines) {
    const n = counts[p.id];
    avgDays[p.id] = n > 0 ? Math.round(sums[p.id] / n) : 0;
  }
  return { avgDays, sampleCounts: counts };
}

export function PipelineAnalytics({
  pipelines, getPropertiesByPipeline, savedProperties, stageHistory,
}: PipelineAnalyticsProps) {
  const stats = useMemo(() => {
    let totalCount = 0; let totalValue = 0; let activeValue = 0;
    const stageCounts: Record<string, number> = {};
    const { avgDays: stageAvgDays, sampleCounts: stageDwellSamples } =
      computeStageAvgDwellDays(pipelines, savedProperties, stageHistory);

    for (const pipeline of pipelines) {
      const props = getPropertiesByPipeline(pipeline);
      stageCounts[pipeline.id] = props.length;
      totalCount += props.length;
      for (const p of props) {
        const price = (p.property_data as unknown as MockProperty)?.price || 0;
        totalValue += price;
        if (!isTerminalPipeline(pipeline)) activeValue += price;
      }
    }
    return { totalCount, totalValue, activeValue, stageCounts, stageAvgDays, stageDwellSamples };
  }, [pipelines, getPropertiesByPipeline, savedProperties, stageHistory]);

  if (stats.totalCount === 0) return null;

  const closedWon  = findClosedWonPipeline(pipelines);
  const closedLost = findClosedLostPipeline(pipelines);
  const closedCount     = closedWon  ? (stats.stageCounts[closedWon.id]  ?? 0) : 0;
  const closedLostCount = closedLost ? (stats.stageCounts[closedLost.id] ?? 0) : 0;
  const activeCount = stats.totalCount - closedCount - closedLostCount;
  const nonTerminal = pipelines.filter((p) => !isTerminalPipeline(p));
  const conversionPct = stats.totalCount > 0
    ? Math.round((closedCount / stats.totalCount) * 100)
    : 0;

  // First two non-terminal stages with data for "Avg Time in Stage" sub text
  const stageTimePairs = nonTerminal
    .filter((p) => (stats.stageDwellSamples[p.id] ?? 0) > 0)
    .slice(0, 2)
    .map((p) => ({ title: p.title, days: stats.stageAvgDays[p.id], color: p.color ?? "#8a93a3" }));

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {/* Total Properties */}
      <div className="bg-card border border-border rounded-[10px] px-4 py-3.5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 grid place-items-center flex-shrink-0">
          <Home className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground font-medium">Total Properties</p>
          <p className="text-[22px] font-extrabold text-foreground leading-tight">{stats.totalCount}</p>
          <p className="text-[10.5px] text-muted-foreground">
            {activeCount} active · {closedCount} closed
          </p>
        </div>
      </div>

      {/* Total Value */}
      <div className="bg-card border border-border rounded-[10px] px-4 py-3.5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-green-500/10 grid place-items-center flex-shrink-0">
          <DollarSign className="h-4 w-4 text-green-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground font-medium">Total Value</p>
          <p className="text-[22px] font-extrabold text-foreground leading-tight">{fmt(stats.totalValue)}</p>
          <p className="text-[10.5px] text-muted-foreground">{fmt(stats.activeValue)} active</p>
        </div>
      </div>

      {/* Avg Time in Stage */}
      <div className="bg-card border border-border rounded-[10px] px-4 py-3.5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-500/10 grid place-items-center flex-shrink-0">
          <Clock className="h-4 w-4 text-amber-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground font-medium">Avg. Time in Stage</p>
          <div className="flex flex-wrap gap-x-2 mt-1">
            {stageTimePairs.length > 0 ? stageTimePairs.map((s) => (
              <span key={s.title} className="text-[10.5px] text-muted-foreground inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                {s.title}:{" "}
                <span className="font-semibold text-foreground">{s.days}d</span>
              </span>
            )) : (
              <span className="text-[10.5px] text-muted-foreground">No data yet</span>
            )}
          </div>
        </div>
      </div>

      {/* Conversion */}
      <div className="bg-card border border-border rounded-[10px] px-4 py-3.5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-blue-500/10 grid place-items-center flex-shrink-0">
          <TrendingUp className="h-4 w-4 text-blue-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground font-medium">Conversion</p>
          <p className="text-[22px] font-extrabold text-foreground leading-tight">{conversionPct}%</p>
          <p className="text-[10.5px] text-muted-foreground">
            {closedCount} of {stats.totalCount} closed
          </p>
        </div>
      </div>
    </div>
  );
}
